<template>
  <div class="my-5">
    <app-heading :text="'CVC Migration'" :heading="'main'"></app-heading>

    <v-alert v-if="accessDenied" type="error" outlined>You need the VispAdmin group for this page.</v-alert>

    <template v-else>
      <v-alert v-if="qaBanner" type="info" dense outlined>{{ qaBanner }}</v-alert>

      <v-row dense align="center">
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="cvcId"
            :items="cvcOptions"
            item-text="label"
            item-value="id"
            label="CVC"
            placeholder="Type to search by name or CVC id"
            outlined
            dense
            hide-details
            clearable
            :loading="loadingLists"
            :disabled="busy"
            @change="onCvcChosen"
          >
            <template v-slot:item="{ item }">
              <span>{{ item.label }}</span>
              <v-chip v-if="item.migrated" x-small class="ml-2">migrated</v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <span>{{ item.label }}</span>
              <v-chip v-if="item.migrated" x-small class="ml-2">migrated</v-chip>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="auto">
          <v-menu v-model="targetMenu" :close-on-content-click="false" offset-y>
            <template v-slot:activator="{ on }">
              <v-btn text small color="primary" :disabled="!cvcId || busy" v-on="on">Change target</v-btn>
            </template>
            <v-card class="pa-3" width="420">
              <v-autocomplete
                v-model="pickedHost"
                :items="hostOptions"
                item-text="label"
                item-value="id"
                label="Target network host"
                placeholder="Type to search by name or IP"
                outlined
                dense
                hide-details
                @change="onTargetChosen"
              />
              <div class="mt-2 caption amber--text text--darken-3">
                The target must be the local POP router for {{ poiState }}. Preflight blocks any other.
              </div>
            </v-card>
          </v-menu>
        </v-col>
        <v-col cols="auto">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn icon :disabled="!cvcId || busy" @click="runPreflight">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Refresh (runs Preflight and writes an audit row)</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-alert v-if="listError" type="error" dense outlined class="mt-2">
        Usher not reachable - CVC list unavailable
        <v-btn small text class="ml-2" @click="loadLists">Retry</v-btn>
      </v-alert>

      <div v-if="!cvcId" class="mt-6 grey--text text--darken-1 text-center">Choose a CVC to start.</div>

      <div v-else :key="cvcId" class="mt-3">
        <div class="cvc-migration-sticky">
          <cvc-migration-header
            :preflight="preflight"
            :cvc="selectedCvc"
            :hosts="hosts"
            :refresh="refresh"
            :changed-at="changedAt"
            :now="now"
            @retry="refreshNow"
            @rollback="openRollback"
          />
        </div>

        <cvc-migration-readiness
          class="mt-3"
          :preflight="preflight"
          :last-verify="lastVerify"
          :error="preflightError"
          :now="now"
          @retry="runPreflight"
        />

        <v-expansion-panels v-model="panels" multiple class="mt-3">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div><strong>A Planning</strong><span class="ml-3 grey--text text--darken-1">window, statics, IP report</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-planning
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :now="now"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>
              <div><strong>B Pre-build and verify</strong><span class="ml-3 grey--text text--darken-1">build, verify, drift, target copies</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-prebuild
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :last-verify="lastVerify"
                :verified-at="lastVerifyAt"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
                @verified="onVerified"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel id="cvc-migration-cutover">
            <v-expansion-panel-header>
              <div><strong>C Cutover</strong><span class="ml-3 grey--text text--darken-1">shut, commit, waiting for Optus, rollback, confirm, activate</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-cutover
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :now="now"
                :last-verify="lastVerify"
                :reconnect="reconnect"
                :reconnect-error="reconnectError"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
                @verified="onVerified"
                @reconnect="onReconnect"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>
              <div><strong>D After cutover</strong><span class="ml-3 grey--text text--darken-1">kick, reconnection, mark migrated</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-after
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :now="now"
                :reconnect="reconnect"
                :reconnect-error="reconnectError"
                :auto-on="autoReconnect"
                :kick-run="kickRun"
                :kick-loop="kickLoop"
                :kick-busy="kickBusy"
                :kick-note="kickNote"
                :kick-row-map="kickRowMap"
                :kick-retry-ids="kickRetryIds"
                :last-kick-run="lastKickAll"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
                @verified="onVerified"
                @reconnect="onReconnect"
                @auto="autoReconnect = $event"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>
              <div><strong>E Clean-up</strong><span class="ml-3 grey--text text--darken-1">remove source copies, cancel</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-cleanup
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :now="now"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel id="cvc-migration-rollback" class="cvc-migration-rollback-panel">
            <v-expansion-panel-header>
              <div><strong>R Rollback</strong><span class="ml-3 grey--text text--darken-1">undo what this migration has done so far</span></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <cvc-migration-rollback
                v-if="preflight"
                :preflight="preflight"
                :cvc-id="cvcId"
                :target-host-id="targetHostId"
                :now="now"
                :reconnect="reconnect"
                :kick-run="kickRun"
                :kick-loop="kickLoop"
                :kick-busy="kickBusy"
                :kick-note="kickNote"
                :busy="busy"
                @busy="busy = $event"
                @changed="onChanged"
                @open-cutover="openCutover"
              />
              <div v-else class="grey--text text--darken-1">Run Preflight first</div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-card v-if="preflight" outlined class="mt-3 pa-3">
          <cvc-migration-avc-grid
            :preflight="preflight"
            :cvc-id="cvcId"
            :target-host-id="targetHostId"
            :last-verify="lastVerify"
            :reconnect-rows="reconnect ? reconnect.rows : null"
            :early-repoint="earlyRepoint"
            :kicked-at="preflight.record ? preflight.record.kickedAt : null"
            :now="now"
            :busy="busy"
            @busy="busy = $event"
            @changed="onChanged"
            @verified="onVerified"
          />
        </v-card>

        <v-card v-if="preflight" outlined class="mt-3 pa-3">
          <cvc-migration-activity :record="preflight.record" :poi-state="poiState" :now="now" :statics="ledgerStatics" />
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { errorMessage, hasPendingEvents, isAccessDenied } from "@/utils/cvcMigration";
import { formatInZone, SYDNEY } from "@/utils/zonedTime";
import {
  CUTOVER_SECTION,
  ROLLBACK_SECTION,
  addSection,
  changedElsewhere,
  hostItems,
  isCurrentAnswer,
  kickUnknownResult,
  kickStampNote,
  lastKickAllRun,
  loadStartup,
  mergeKickRows,
  mergeVerifyInto,
  openSection,
  pickerItems,
  refreshIntervalMs,
  refreshStatus,
  repointedEarly,
  runKick,
  shouldAdoptKickRun,
  shouldPollReconnect,
  shouldReadKickRun,
  shouldRefreshNow,
} from "@/utils/cvcMigrationPage";
import { initialSelection, readLastCvc, selectionQuery, writeLastCvc } from "@/utils/cvcMigrationSelection";
import CvcMigrationHeader from "@/components/cvcMigration/cvc-migration-header";
import CvcMigrationReadiness from "@/components/cvcMigration/cvc-migration-readiness";
import CvcMigrationPlanning from "@/components/cvcMigration/cvc-migration-planning";
import CvcMigrationPrebuild from "@/components/cvcMigration/cvc-migration-prebuild";
import CvcMigrationCutover from "@/components/cvcMigration/cvc-migration-cutover";
import CvcMigrationAfter from "@/components/cvcMigration/cvc-migration-after";
import CvcMigrationCleanup from "@/components/cvcMigration/cvc-migration-cleanup";
import CvcMigrationRollback from "@/components/cvcMigration/cvc-migration-rollback";
import CvcMigrationAvcGrid from "@/components/cvcMigration/cvc-migration-avc-grid";
import CvcMigrationActivity from "@/components/cvcMigration/cvc-migration-activity";

const TICK_MS = 5000;
// An adopted run that fails is not tried again for this long.
const ADOPT_RETRY_MS = 60000;

// The page owns the CVC, the target, the Preflight answer and the refresh
// timer. Every section gets the answer as a prop, runs its own actions and
// tells the page when one is running (`busy`) and when to reload (`changed`).
export default {
  name: "CvcMigration",
  components: {
    CvcMigrationHeader,
    CvcMigrationReadiness,
    CvcMigrationPlanning,
    CvcMigrationPrebuild,
    CvcMigrationCutover,
    CvcMigrationAfter,
    CvcMigrationCleanup,
    CvcMigrationRollback,
    CvcMigrationAvcGrid,
    CvcMigrationActivity,
  },
  // The one owner of Kick all and Kick this: Section D and the AVC grid ask it
  // to start a run, so the run, its progress and its rows survive either panel
  // closing (a run lives on the server and any open page continues it).
  provide () {
    return { cvcKickStart: this.startKick };
  },
  data: () => ({
    environment: null,
    cvcs: [],
    hosts: [],
    loadingLists: false,
    listError: false,
    accessDenied: false,
    cvcId: null,
    // 0 asks the server to resolve the target; every later call sends the answer.
    targetHostId: 0,
    preflight: null,
    preflightError: "",
    lastRefreshAt: null,
    lastAttemptMs: 0,
    staleSince: null,
    staleMessage: "",
    knownUpdatedAt: null,
    ownAction: false,
    changedAt: "",
    busy: false,
    refreshing: false,
    refreshAgain: false,
    lastVerify: null,
    lastVerifyAt: null,
    panels: [0],
    targetMenu: false,
    pickedHost: null,
    // The reconnection check (read-only): the page polls it while the cutover
    // needs it and hands the last answer to sections C, D and the grid.
    reconnect: null,
    reconnectError: "",
    lastReconnectMs: 0,
    autoReconnect: true,
    pollingReconnect: false,
    // The kick run of the CVC (latest snapshot), whether this tab runs a loop
    // for it, the latest kick per service, and the runs this tab has seen.
    kickRun: null,
    kickLoop: false,
    kickBusy: false,
    kickNote: "",
    kickRetryIds: [],
    kickRowMap: {},
    kickSeen: [],
    kickSentIds: [],
    kickReading: false,
    kickAdoptAfterMs: 0,
    now: new Date(),
    timer: null,
  }),
  computed: {
    qaBanner () {
      const env = this.environment;
      if (!env || env.networkStepsEnabled !== false) return "";
      return `QA (${env.environmentName}): router, Usher write and RADIUS steps are disabled here (Production only). This page proves the wiring and the error messages.`;
    },
    cvcOptions () {
      return pickerItems(this.cvcs, this.hosts);
    },
    hostOptions () {
      return hostItems(this.hosts);
    },
    selectedCvc () {
      return this.cvcs.find(c => c.id === this.cvcId) || null;
    },
    poiState () {
      if (this.preflight) return this.preflight.poiState;
      return this.selectedCvc ? this.selectedCvc.poiState || "" : "";
    },
    phase () {
      return this.preflight && this.preflight.steps ? this.preflight.steps.phase : "none";
    },
    pendingEvents () {
      return hasPendingEvents(this.preflight);
    },
    refreshInterval () {
      return refreshIntervalMs(this.phase, this.pendingEvents);
    },
    kickedAt () {
      return this.preflight && this.preflight.record ? this.preflight.record.kickedAt : null;
    },
    // The statics Usher remembers for the record: the activity table names their runs.
    ledgerStatics () {
      return (this.preflight && this.preflight.rollback && this.preflight.rollback.statics) || [];
    },
    // Sessions are on the target before the shut: the grid does not force remove a live copy.
    earlyRepoint () {
      return repointedEarly(this.preflight, this.reconnect);
    },
    lastKickAll () {
      return lastKickAllRun(this.kickSeen);
    },
    refresh () {
      return refreshStatus({
        lastRefreshAt: this.lastRefreshAt,
        staleSince: this.staleSince,
        staleMessage: this.staleMessage,
        intervalMs: this.refreshInterval,
      });
    },
  },
  watch: {
    // When the phase moves on, open the section that now matters.
    phase (next) {
      this.panels = addSection(this.panels, openSection(next));
    },
    // A kick stamped by another tab or operator: read the run behind it.
    kickedAt (next, previous) {
      if (next !== previous) this.readKickRun();
    },
  },
  mounted () {
    this.loadLists();
    this.timer = setInterval(this.tick, TICK_MS);
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationEnvironment",
      "cvcMigrationListCvcs",
      "cvcMigrationListHosts",
      "cvcMigrationPreflight",
      "cvcMigrationRefresh",
      "cvcMigrationVerifyReconnected",
      "cvcMigrationKick",
      "cvcMigrationKickRun",
    ]),
    noteFailure (e) {
      if (isAccessDenied(e)) this.accessDenied = true;
    },
    // The environment, the CVC list and the hosts load together, without the
    // global loader. Each answer shows as it arrives (the QA banner does not
    // wait for the lists, the lists do not wait for the environment).
    async loadLists () {
      this.loadingLists = true;
      this.listError = false;
      try {
        await loadStartup(
          {
            environment: () => this.cvcMigrationEnvironment(),
            cvcs: () => this.cvcMigrationListCvcs(),
            hosts: () => this.cvcMigrationListHosts(),
          },
          {
            environment: environment => { this.environment = environment; },
            cvcs: cvcs => { if (cvcs) this.cvcs = cvcs; },
            hosts: hosts => { if (hosts) this.hosts = hosts; },
            failed: (name, e) => {
              this.noteFailure(e);
              if (name === "cvcs") this.listError = true;
            },
          }
        );
        this.restoreSelection();
      } finally {
        this.loadingLists = false;
      }
    },
    // Once the lists are in: open the CVC (and target) of the page link, or the
    // last CVC chosen in this browser. Nothing is opened when the operator has
    // already picked one, and an id the lists do not know leaves the picker.
    restoreSelection () {
      if (this.cvcId || this.accessDenied) return;
      const pick = initialSelection({ query: this.$route.query, stored: readLastCvc(), cvcs: this.cvcs, hosts: this.hosts });
      if (!pick) return;
      this.cvcId = pick.cvcId;
      this.selectCvc(pick.cvcId, pick.targetHostId);
      this.remember(pick.cvcId, pick.targetHostId);
    },
    // The chosen CVC and target go into the page link (`replace`: no history
    // entry; a link that is already right is not an error) and the CVC into the browser.
    remember (cvcId, targetHostId) {
      writeLastCvc(cvcId);
      this.$router.replace({ query: selectionQuery(this.$route.query, { cvcId, targetHostId }) }).catch(() => {});
    },
    onCvcChosen (id) {
      this.selectCvc(id, 0);
      this.remember(id, 0);
    },
    // Starts over for a CVC: nothing of the last one stays, then Preflight.
    selectCvc (id, targetHostId) {
      this.preflight = null;
      this.preflightError = "";
      this.lastVerify = null;
      this.lastVerifyAt = null;
      this.reconnect = null;
      this.reconnectError = "";
      this.lastReconnectMs = 0;
      this.resetKick();
      this.lastRefreshAt = null;
      this.staleSince = null;
      this.knownUpdatedAt = null;
      this.changedAt = "";
      // 0 asks the server to resolve the target.
      this.targetHostId = targetHostId;
      this.refreshAgain = false;
      this.panels = [0];
      if (id) this.runPreflight();
    },
    // The header button: open panel R and bring it into view.
    openRollback () {
      this.panels = addSection(this.panels, ROLLBACK_SECTION);
      this.$nextTick(() => {
        const panel = document.getElementById("cvc-migration-rollback");
        if (panel && panel.scrollIntoView) panel.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    // "Retry the shut" in panel R: open section C and bring it into view.
    openCutover () {
      this.panels = addSection(this.panels, CUTOVER_SECTION);
      this.$nextTick(() => {
        const panel = document.getElementById("cvc-migration-cutover");
        if (panel && panel.scrollIntoView) panel.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    onTargetChosen (hostId) {
      this.targetMenu = false;
      if (!hostId) return;
      this.targetHostId = hostId;
      this.remember(this.cvcId, hostId);
      this.runPreflight();
    },
    // What a call asks for, kept to tell whether its answer is still wanted.
    asked () {
      return { cvcId: this.cvcId, targetHostId: this.targetHostId };
    },
    // The operator may have picked another CVC or target while a call was out.
    stillAsked (asked) {
      return isCurrentAnswer(asked, this.asked());
    },
    // The audited Preflight: on a CVC pick, the target change and the refresh
    // button. It shows the global loader.
    async runPreflight () {
      if (!this.cvcId || this.accessDenied) return;
      const asked = this.asked();
      this.refreshing = true;
      try {
        const answer = await this.cvcMigrationPreflight(asked);
        if (this.stillAsked(asked)) this.apply(answer, true);
      } catch (e) {
        this.noteFailure(e);
        if (!this.stillAsked(asked)) return;
        if (this.preflight) this.markStale(e);
        else this.preflightError = errorMessage(e);
      } finally {
        this.refreshing = false;
      }
    },
    // The silent Preflight: no modal, no toast, no audit row.
    async refreshNow () {
      if (!this.cvcId || this.accessDenied) return;
      if (this.refreshing) {
        this.refreshAgain = true;
        return;
      }
      const asked = this.asked();
      this.refreshing = true;
      this.lastAttemptMs = Date.now();
      try {
        const answer = await this.cvcMigrationRefresh(asked);
        if (this.stillAsked(asked)) this.apply(answer, false);
      } catch (e) {
        this.noteFailure(e);
        if (this.stillAsked(asked)) this.markStale(e);
      } finally {
        this.refreshing = false;
        if (this.refreshAgain) {
          this.refreshAgain = false;
          this.refreshNow();
        }
      }
    },
    apply (data, own) {
      const first = !this.preflight;
      this.preflight = data;
      if (data.targetNetworkHostId) this.targetHostId = data.targetNetworkHostId;
      this.lastRefreshAt = new Date();
      this.lastAttemptMs = Date.now();
      this.staleSince = null;
      this.staleMessage = "";
      this.preflightError = "";
      const updatedAt = data.record ? data.record.updatedAt : null;
      if (!first && changedElsewhere(this.knownUpdatedAt, updatedAt, own || this.ownAction)) {
        this.changedAt = `${formatInZone(updatedAt, SYDNEY).split(" ")[1]} SYD`;
      }
      this.knownUpdatedAt = updatedAt;
      this.ownAction = false;
      if (first) this.panels = [openSection(this.phase)];
      if (first && data.record) this.readKickRun();
    },
    markStale (e) {
      if (!this.staleSince) this.staleSince = new Date();
      this.staleMessage = errorMessage(e);
    },
    // A section finished an action (or failed one): reload quietly.
    onChanged () {
      this.ownAction = true;
      this.refreshNow();
    },
    onVerified ({ merged, partial }) {
      this.lastVerify = partial ? mergeVerifyInto(this.lastVerify, merged) : merged;
      this.lastVerifyAt = new Date();
    },
    // A section read the reconnection check itself (its Refresh button).
    onReconnect (answer) {
      this.reconnect = answer;
      this.reconnectError = "";
      this.lastReconnectMs = Date.now();
    },
    // The silent read: no modal, no toast, no audit row. A failure keeps the
    // last answer and shows its reason beside it.
    async pollReconnect () {
      this.pollingReconnect = true;
      this.lastReconnectMs = Date.now();
      // The check sends the CVC only, so the target is not part of what was asked.
      const asked = { cvcId: this.cvcId };
      try {
        const answer = await this.cvcMigrationVerifyReconnected({ ...asked, refresh: true });
        if (!isCurrentAnswer(asked, { cvcId: this.cvcId })) return;
        this.reconnect = answer;
        this.reconnectError = "";
      } catch (e) {
        this.noteFailure(e);
        if (isCurrentAnswer(asked, { cvcId: this.cvcId })) this.reconnectError = errorMessage(e);
      } finally {
        this.pollingReconnect = false;
      }
    },
    resetKick () {
      this.kickRun = null;
      this.kickLoop = false;
      this.kickBusy = false;
      this.kickNote = "";
      this.kickRetryIds = [];
      this.kickRowMap = {};
      this.kickSeen = [];
      this.kickSentIds = [];
      this.kickAdoptAfterMs = 0;
    },
    // Keep the newest snapshot, the latest kick per service and the runs seen.
    applyKickRun (run) {
      if (!run || run.status === "none") {
        this.kickRun = null;
        return;
      }
      this.kickRun = run;
      this.kickRowMap = mergeKickRows(this.kickRowMap, run);
      this.kickSeen = this.kickSeen.filter(r => r.runId !== run.runId).concat(run).slice(-20);
    },
    // The silent read of the run (no audit row, no Usher call). A run that sat
    // idle for 15 s (its page was closed or lost its answer) is continued from here.
    async readKickRun () {
      if (!this.cvcId || this.kickReading || this.kickLoop || this.accessDenied) return;
      const asked = { cvcId: this.cvcId };
      this.kickReading = true;
      try {
        const run = await this.cvcMigrationKickRun(asked);
        if (!isCurrentAnswer(asked, { cvcId: this.cvcId }) || this.kickLoop) return;
        this.applyKickRun(run);
        if (shouldAdoptKickRun(this.kickRun, this.kickLoop) && Date.now() >= this.kickAdoptAfterMs) this.adoptKick();
      } catch (e) {
        this.noteFailure(e);
      } finally {
        this.kickReading = false;
      }
    },
    async adoptKick () {
      try {
        await this.driveKick({ runId: this.kickRun.runId, adopt: true });
      } catch (e) {
        this.kickAdoptAfterMs = Date.now() + ADOPT_RETRY_MS;
        this.kickNote = errorMessage(e);
      }
    },
    // Kick all and Kick this: one run, driven from here. A refusal is thrown to
    // the caller, which shows it in its own alert; every other end is a result.
    startKick ({ ids, kickAll, restored, follow = true }) {
      if (this.kickLoop) return Promise.resolve({ kind: "busy", run: this.kickRun, message: "" });
      return this.driveKick({ ids, kickAll, restored, follow });
    },
    async driveKick (options) {
      const asked = { cvcId: this.cvcId };
      const current = () => isCurrentAnswer(asked, { cvcId: this.cvcId });
      this.kickLoop = true;
      this.kickNote = "";
      this.kickRetryIds = [];
      if (options.ids) this.kickSentIds = options.ids.slice();
      let result;
      try {
        result = await runKick({
          ...options,
          send: body => this.cvcMigrationKick({ cvcId: asked.cvcId, ...body }),
          read: () => this.cvcMigrationKickRun({ cvcId: asked.cvcId }),
          onProgress: ({ run, busy }) => {
            if (!current()) return;
            this.applyKickRun(run);
            this.kickBusy = busy;
          },
          shouldStop: () => !current(),
        });
      } finally {
        this.kickLoop = false;
        this.kickBusy = false;
      }
      if (result.kind === "error") throw result.error;
      if (!current()) return result;
      if (result.run) this.applyKickRun(result.run);
      if (result.kind === "unknown") {
        const unknown = kickUnknownResult(this.kickRun, this.kickSentIds);
        this.kickNote = result.message || unknown.message;
        this.kickRetryIds = unknown.retryIds;
      } else if (result.kind === "max") {
        this.kickNote = result.message;
      } else {
        this.kickNote = kickStampNote(result.run);
      }
      return result;
    },
    tick () {
      this.now = new Date();
      const visible = document.visibilityState === "visible";
      const due = shouldRefreshNow({
        nowMs: Date.now(),
        lastMs: this.lastAttemptMs,
        intervalMs: this.refreshInterval,
        visible,
        busy: this.busy || this.refreshing,
        enabled: !!this.preflight && !this.accessDenied,
      });
      if (due) this.refreshNow();
      const poll = shouldPollReconnect({
        preflight: this.preflight,
        reconnect: this.reconnect,
        autoOn: this.autoReconnect,
        nowMs: Date.now(),
        lastMs: this.lastReconnectMs,
        visible,
        busy: this.busy || this.pollingReconnect || this.accessDenied,
      });
      if (poll) this.pollReconnect();
      // Not gated on visibility: a hidden tab keeps a running run alive.
      if (shouldReadKickRun({ run: this.kickRun, kickLoop: this.kickLoop })) this.readKickRun();
    },
  },
};
</script>

<style scoped>
.cvc-migration-sticky {
  position: sticky;
  top: 64px;
  z-index: 5;
}
.cvc-migration-rollback-panel {
  border-left: 4px solid #c62828;
}
</style>
